import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { userManagementService } from '@/api/services/UserManagementService';
import { onSessionExpired, restoreSession } from '@/auth/refreshManager';
import { tokenStore } from '@/auth/tokenStore';
import type { AuthTokens, LoginRequest } from '@/types/auth';

interface AuthContextValue {
  readonly isAuthenticated: boolean;
  /** true пока идёт восстановление сессии при загрузке (refresh). Гарды ждут его. */
  readonly isInitializing: boolean;
  readonly login: (credentials: LoginRequest) => Promise<void>;
  /** Применить уже полученные токены (например, после подтверждения email). */
  readonly loginWithTokens: (tokens: AuthTokens) => void;
  readonly logout: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// React-обвязка над сессией: владеет флагом isAuthenticated. Хранение/refresh/single-flight —
// в src/auth/*. Данные профиля — через useProfile (server-state), не здесь.
export function AuthProvider({ children }: { readonly children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const queryClient = useQueryClient();

  const logout = useCallback(() => {
    tokenStore.clear();
    // Чистим server-state, чтобы данные прошлой сессии не «протекли» в следующую.
    queryClient.clear();
    setIsAuthenticated(false);
  }, [queryClient]);

  const login = useCallback(async (credentials: LoginRequest) => {
    const tokens = await userManagementService.login(credentials);
    tokenStore.setTokens(tokens);
    setIsAuthenticated(true);
  }, []);

  const loginWithTokens = useCallback((tokens: AuthTokens) => {
    tokenStore.setTokens(tokens);
    setIsAuthenticated(true);
  }, []);

  // Восстановление сессии после перезагрузки по сохранённому refresh-токену.
  useEffect(() => {
    restoreSession()
      .then(setIsAuthenticated)
      .catch(() => setIsAuthenticated(false))
      .finally(() => setIsInitializing(false));
  }, []);

  // Транспорт сообщил, что сессия мертва (refresh не удался) → разлогиниваемся.
  useEffect(() => onSessionExpired(logout), [logout]);

  // Кросс-таб: очистка refresh-токена в другой вкладке разлогинивает и эту.
  useEffect(() => tokenStore.subscribeCrossTabClear(logout), [logout]);

  const value = useMemo<AuthContextValue>(
    () => ({ isAuthenticated, isInitializing, login, loginWithTokens, logout }),
    [isAuthenticated, isInitializing, login, loginWithTokens, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

// Безопасный доступ к сессии без гарантии провайдера: для низкоуровневых презентационных компонентов
// (и их изолированных тестов), которые могут отрисовываться вне AuthProvider. Возвращает undefined
// вместо исключения. В рабочем приложении провайдер всегда есть — там это полноценная сессия.
export function useOptionalAuth(): AuthContextValue | undefined {
  return useContext(AuthContext);
}
